// 1. React
import React, { useState, useEffect, useCallback } from 'react';

// 2. Third-party
import { Table, Button, Tag, Space, Popconfirm, message, Modal, Select, Empty, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

// 3. Services
import { inductionRequirementService, visitorInductionService } from '@iso/services';
import { getFormPurposeLabel } from '@iso/constants/formPurposes';

const { Option } = Select;

// visitor_type on a requirement. null/'' means the requirement applies to
// everyone — which also covers self-service check-ins, where visitor_info
// .visitor_type is frequently NULL and a typed rule would never match.
const APPLIES_TO_ALL = 'all';

const APPLIES_TO_LABEL = {
  visitor: 'Visitors',
  contractor: 'Contractors',
};

const appliesToLabel = (visitorType) =>
  APPLIES_TO_LABEL[visitorType] || 'Everyone';

/**
 * Property Settings -> Inductions.
 *
 * Declares which inductions this property requires. With none configured the
 * Visitors badge keeps its original behaviour (the visitor's most recent
 * induction); as soon as there is one, the badge reports "N of M held" instead.
 * That opt-in is deliberate — every property starts here empty.
 */
const InductionRequirementsManagement = ({ propertyId }) => {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addVisible, setAddVisible] = useState(false);
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(undefined);
  const [selectedAppliesTo, setSelectedAppliesTo] = useState(APPLIES_TO_ALL);

  const fetchRequirements = useCallback(() => {
    if (!propertyId) return;
    setLoading(true);
    inductionRequirementService
      .getList(propertyId)
      .then((res) => {
        setRequirements((res && res.success && res.data && res.data.requirements) || []);
      })
      .catch(() => message.error('Could not load induction requirements'))
      .finally(() => setLoading(false));
  }, [propertyId]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const openAdd = () => {
    setSelectedFormId(undefined);
    setSelectedAppliesTo(APPLIES_TO_ALL);
    setAddVisible(true);
    setFormsLoading(true);
    // Reuses the picker the Run Induction modal uses — a form is offered only
    // if it has an induction purpose and questions to render, so a requirement
    // can never name something that cannot actually be run.
    visitorInductionService
      .getAvailableForms(propertyId)
      .then((res) => {
        setForms((res && res.success && res.data && res.data.forms) || []);
      })
      .catch(() => message.error('Could not load induction forms'))
      .finally(() => setFormsLoading(false));
  };

  const handleAdd = () => {
    if (!selectedFormId) {
      message.warning('Choose an induction first');
      return;
    }
    setSaving(true);
    inductionRequirementService
      .create({
        propertyId,
        formId: selectedFormId,
        visitorType: selectedAppliesTo === APPLIES_TO_ALL ? null : selectedAppliesTo,
      })
      .then((res) => {
        if (res && res.success) {
          message.success(res.message || 'Requirement added');
          setAddVisible(false);
          fetchRequirements();
        } else {
          message.error((res && res.message) || 'Could not add the requirement');
        }
      })
      .catch(() => message.error('Could not add the requirement'))
      .finally(() => setSaving(false));
  };

  const handleToggleActive = (row, checked) => {
    inductionRequirementService
      .update(row.id, { is_active: checked })
      .then((res) => {
        if (res && res.success) {
          fetchRequirements();
        } else {
          message.error((res && res.message) || 'Could not update the requirement');
        }
      })
      .catch(() => message.error('Could not update the requirement'));
  };

  const handleRemove = (row) => {
    inductionRequirementService
      .remove(row.id)
      .then((res) => {
        if (res && res.success) {
          message.success(res.message || 'Requirement removed');
          fetchRequirements();
        } else {
          message.error((res && res.message) || 'Could not remove the requirement');
        }
      })
      .catch(() => message.error('Could not remove the requirement'));
  };

  const columns = [
    {
      title: 'Induction',
      dataIndex: 'form_name',
      key: 'form_name',
      render: (name, row) => name || getFormPurposeLabel(row.purpose) || '-',
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
      render: (purpose) => (purpose ? <Tag>{getFormPurposeLabel(purpose)}</Tag> : '-'),
    },
    {
      title: 'Applies to',
      dataIndex: 'visitor_type',
      key: 'visitor_type',
      render: (visitorType) => appliesToLabel(visitorType),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, row) => (
        <Switch
          size="small"
          checked={!!isActive}
          onChange={(checked) => handleToggleActive(row, checked)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <Popconfirm
          title="Remove this requirement?"
          onConfirm={() => handleRemove(row)}
          okText="Remove"
          cancelText="Cancel"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <span style={{ color: '#667085' }}>
          Inductions every visitor must hold before they count as inducted.
        </span>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          Add requirement
        </Button>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={requirements}
        loading={loading}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No requirements set — the induction badge shows each visitor's most recent induction."
            />
          ),
        }}
      />

      <Modal
        title="Add induction requirement"
        open={addVisible}
        onCancel={() => setAddVisible(false)}
        onOk={handleAdd}
        okText="Add"
        confirmLoading={saving}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>Induction</div>
          <Select
            style={{ width: '100%' }}
            placeholder="Select an induction"
            loading={formsLoading}
            value={selectedFormId}
            onChange={setSelectedFormId}
            showSearch
            optionFilterProp="label"
            notFoundContent={
              formsLoading
                ? null
                : 'No induction forms on this property. Create one in Forms and set its purpose to an induction type.'
            }
          >
            {forms.map((f) => (
              <Option key={f.id} value={f.id} label={f.name}>
                {f.name}
                <span style={{ color: '#98a2b3' }}> · {getFormPurposeLabel(f.purpose)}</span>
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>Applies to</div>
          <Select style={{ width: '100%' }} value={selectedAppliesTo} onChange={setSelectedAppliesTo}>
            <Option value={APPLIES_TO_ALL}>Everyone</Option>
            <Option value="visitor">Visitors</Option>
            <Option value="contractor">Contractors</Option>
          </Select>
          {/* Self-service check-ins often leave visitor_type NULL, and those
              visitors match only the "Everyone" rule. */}
          <div style={{ marginTop: 6, fontSize: 12, color: '#98a2b3' }}>
            Visitors who checked themselves in may have no recorded type — only
            "Everyone" requirements apply to them.
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InductionRequirementsManagement;
